/**
 * /app/$tenantSlug/wfh/$id — WFH request detail (migration 063).
 *
 * Read-only detail with the lifecycle fields (the request's own status +
 * decision fields ARE the timeline; there is no separate timeline table,
 * mirroring leave). Cancel is available to the requester for a
 * pending/approved request.
 */

import { useState } from "react";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { RequestSurfaceClaim } from "@/components/dashboard/surface-claim";
import { fromSearchSchema } from "@/lib/from-search";
import { decidedByLabel } from "@/lib/approval-copy";
import { toApiError } from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useApproveWFHRequest,
  useCancelWFHRequest,
  useWFHRequest,
} from "@/hooks/use-wfh";
import { SelfApproveCallout } from "@/components/approvals/self-approve-callout";
import { halfDayLabel } from "@/lib/leave-format";
import { formatDate, formatDateRange } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { requestDetailBack } from "@/lib/request-detail-nav";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/wfh/$id")({
  // ?from=me|manage|inbox — the side that opened it (lib/from-search.ts).
  validateSearch: fromSearchSchema,
  component: WFHDetailRoute,
});

function WFHDetailRoute() {
  const { id } = useParams({ from: "/_authgated/app/$tenantSlug/wfh/$id" });
  const numId = Number(id);
  const q = useWFHRequest(numId);
  const { user } = useAuth();
  const cancel = useCancelWFHRequest();
  const approve = useApproveWFHRequest();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const { tenantSlug } = useParams({ from: "/_authgated/app/$tenantSlug" });
  // Back follows ?from= (Me / Manage / Inbox), as leave does. Phones only:
  // the desktop breadcrumb leads up there.
  const { from } = Route.useSearch();
  const back = requestDetailBack({
    kind: "wfh",
    from,
    isOwner: q.data && user ? q.data.user_id === user.user_id : null,
  });

  async function doCancel() {
    try {
      await cancel.mutateAsync(numId);
      toast.success("WFH request cancelled.");
      setConfirmCancel(false);
    } catch (e) {
      // The server says why (e.g. it has already started) — not a bare
      // "Failed to cancel".
      toast.error(toApiError(e).message);
    }
  }

  return (
    <TimeAndLeaveShell
      // The record: "WFH Request — Meera Joshi, 3–5 Nov 2026" (no name on
      // your own), so the breadcrumb and the tab title name it.
      title={
        q.data
          ? `WFH Request — ${
              q.data.user_id !== user?.user_id && q.data.user_name ? `${q.data.user_name}, ` : ""
            }${formatDateRange(q.data.start_date, q.data.end_date)}`
          : "WFH Request"
      }
      description=""
      actions={
        <Button asChild variant="outline" className="gap-1.5 lg:hidden">
          <Link to={back.to} params={{ tenantSlug }} search={back.search}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            {back.label}
          </Link>
        </Button>
      }
    >
      <LeaveFeatureGate>
        {q.isLoading && <Skeleton className="h-40 w-full max-w-2xl" />}
        {/* An approver viewing someone's request stays on Inbox (QA #15). */}
        {q.data && <RequestSurfaceClaim ownerId={q.data.user_id} />}
        {q.data && (
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                {q.data.half_day
                  ? `${formatDate(q.data.start_date)} · ${halfDayLabel(q.data.half_day)}`
                  : formatDateRange(q.data.start_date, q.data.end_date)}
                <StatusBadge status={q.data.status} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Requested by: </span>
                {q.data.user_name ?? `User #${q.data.user_id}`}
              </p>
              <p>
                <span className="text-muted-foreground">Working days: </span>
                {q.data.total_days}{" "}
                <span className="text-muted-foreground">
                  (no leave balance used)
                </span>
              </p>
              {q.data.reason && (
                <p>
                  <span className="text-muted-foreground">Reason: </span>
                  {q.data.reason}
                </p>
              )}
              {q.data.decided_by_id && (
                <p>
                  <span className="text-muted-foreground">Decided by: </span>
                  {decidedByLabel(
                    q.data.decided_by_name ?? `User #${q.data.decided_by_id}`,
                    q.data.decided_by_id,
                    q.data.user_id,
                  )}
                </p>
              )}
              {q.data.decision_notes && (
                <p>
                  <span className="text-muted-foreground">Notes: </span>
                  {q.data.decision_notes}
                </p>
              )}
              {q.data.status === "pending" && q.data.can_self_approve && (
                <div className="pt-3">
                  <SelfApproveCallout
                    noun="WFH request"
                    consequence="The day is marked WFH on your attendance."
                    onApprove={() =>
                      approve.mutateAsync({
                        id: numId,
                        body: { self_approve: true },
                      })
                    }
                  />
                </div>
              )}
              {(q.data.status === "pending" ||
                q.data.status === "approved") && (
                <div className="pt-3">
                  <Button
                    variant="outline"
                    disabled={cancel.isPending}
                    onClick={() => setConfirmCancel(true)}
                  >
                    Cancel Request
                  </Button>
                </div>
              )}
              <AlertDialog
                open={confirmCancel}
                onOpenChange={(o) => {
                  if (!o && !cancel.isPending) setConfirmCancel(false);
                }}
              >
                <AlertDialogContent className="sm:max-w-md">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel this WFH request?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {q.data.status === "approved"
                        ? "The request is cancelled and its approved work-from-home days are also removed from your attendance. You can raise a new request later."
                        : "The request is withdrawn before anyone decides on it. You can raise a new one later."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={cancel.isPending}>Keep It</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => {
                        e.preventDefault();
                        void doCancel();
                      }}
                      disabled={cancel.isPending}
                      className={cn(buttonVariants({ variant: "destructive" }))}
                    >
                      Cancel Request
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        )}
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
